import { useState, useEffect } from "react";
import API, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pill, Lock, ArrowRight } from "lucide-react";

export default function SiteGate({ children }) {
  // Default to "gate OFF" until proven otherwise. Previously this was
  // `null` (showed a spinner) and any network failure fell through to a
  // fail-CLOSED default that flashed the gate UI on every visitor whose
  // first API call hiccuped (CSP block on cross-origin custom domains,
  // Cloudflare 401, slow service-worker boot, etc). For a public landing
  // page this is the wrong tradeoff — a transient backend error must
  // never lock real customers out. Fail-OPEN: render content immediately;
  // only flip the gate ON if the server explicitly says `enabled: true`.
  const [gateEnabled, setGateEnabled] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Always re-check the server. We deliberately do NOT short-circuit on
    // sessionStorage — if the server has flipped the gate off, a previously-
    // unlocked visitor would otherwise stay stuck with `gateEnabled=true`
    // and never realize the gate is gone.
    const sessionUnlocked = sessionStorage.getItem("glpflo_gate") === "unlocked";
    if (sessionUnlocked) setUnlocked(true);

    const apply = (data) => {
      if (data && typeof data.enabled === "boolean") {
        setGateEnabled(data.enabled);
        if (!data.enabled) setUnlocked(true);
      }
    };

    API.get("/site-gate/status")
      .then(({ data }) => apply(data))
      .catch(() => {
        // Cross-origin custom-domain CSP blocks → try the same-origin
        // relative path. If THAT also fails, swallow the error and leave
        // `gateEnabled=false` (fail-OPEN). The default is already correct.
        fetch("/api/site-gate/status")
          .then((r) => r.json())
          .then(apply)
          .catch(() => { /* fail-OPEN, no-op */ });
      });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      try {
        await API.post("/site-gate/verify", { password });
      } catch {
        // Fallback to relative path for custom domain
        const res = await fetch("/api/site-gate/verify", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ password })
        });
        if (!res.ok) throw new Error("Invalid");
      }
      sessionStorage.setItem("glpflo_gate", "unlocked");
      setUnlocked(true);
    } catch (e) {
      setError("Invalid access code. Please try again.");
    } finally { setLoading(false); }
  };

  // Still checking
  if (gateEnabled === null) {
    return null; // fail-OPEN: render nothing instead of flashing a gate while loading
  }

  // Gate disabled or unlocked
  if (!gateEnabled || unlocked) return children;

  // Show gate
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-100 flex items-center justify-center p-4" data-testid="site-gate">
      <div className="w-full max-w-sm text-center">
        <div className="flex items-center gap-2 justify-center mb-8">
          <div className="w-12 h-12 rounded-full bg-purple-600 flex items-center justify-center">
            <Pill className="w-6 h-6 text-white" />
          </div>
          <span className="font-bold text-3xl tracking-tight text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
            GLP<span className="text-purple-600">Flo</span>
          </span>
        </div>

        <div className="bg-white rounded-2xl border border-purple-100 shadow-[0_8px_30px_rgb(0,0,0,0.06)] p-8">
          <div className="w-14 h-14 rounded-2xl bg-purple-100 flex items-center justify-center mx-auto mb-5">
            <Lock className="w-7 h-7 text-purple-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Access Required</h2>
          <p className="text-slate-500 text-sm mb-6">Enter the access code to continue.</p>

          {error && (
            <div className="bg-red-50 text-red-700 border border-red-200 rounded-xl px-4 py-3 text-sm mb-4" data-testid="gate-error">{error}</div>
          )}

          <form onSubmit={handleSubmit}>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Access code"
              className="rounded-xl mb-4 text-center text-lg tracking-widest"
              autoFocus
              required
              data-testid="gate-password-input"
            />
            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-purple-600 hover:bg-purple-700 text-white py-5 text-base"
              data-testid="gate-submit-btn"
            >
              {loading ? "Verifying..." : "Enter"} {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </form>
        </div>

        <p className="text-xs text-slate-400 mt-6">GLPFlo Telehealth Platform</p>
      </div>
    </div>
  );
}
